import { Link, useRouterState } from "@tanstack/react-router";
import {
  Home,
  Calendar,
  QrCode,
  Bell,
  Building2,
  ScanLine,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { MobileMenuSheet } from "./MobileMenuSheet";
import { trpc } from "@/lib/trpc";
import { useCommunity } from "@/contexts/CommunityContext";

const residentNavItems = [
  { to: "/", icon: Home, label: "Inicio" },
  { to: "/amenities", icon: Calendar, label: "Amenities" },
  { to: "/invitations", icon: QrCode, label: "Invitados" },
] as const;

const securityNavItems = [
  { to: "/", icon: Home, label: "Inicio" },
  { to: "/guard", icon: ScanLine, label: "Acceso" },
] as const;

export function MobileBottomNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { selectedCommunityId } = useCommunity();

  const { data: communities } = trpc.amenity.getUserCommunities.useQuery();
  const currentCommunity = communities?.find(
    (c) => c.id === selectedCommunityId,
  );
  const isSecurityGuard = currentCommunity?.role === "SECURITY";

  const { data: unreadData } = trpc.communication.getUnreadCount.useQuery(
    { communityId: selectedCommunityId! },
    {
      enabled: !!selectedCommunityId,
      refetchInterval: 30000,
    },
  );

  const unreadCount = unreadData?.count ?? 0;

  const isActive = (path: string) => {
    if (path === "/") return currentPath === "/";
    return currentPath.startsWith(path);
  };

  const navItems = isSecurityGuard ? securityNavItems : residentNavItems;
  const leftItems = navItems.slice(0, 2);
  const rightItems = isSecurityGuard ? [] : residentNavItems.slice(2);

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-card/95 backdrop-blur-md shadow-[0_-1px_3px_rgba(0,0,0,0.05)] md:hidden">
        <div className="flex h-[72px] items-center justify-around px-2">
          {/* Left items */}
          {leftItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-1 py-2",
                isActive(item.to)
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-xs">{item.label}</span>
            </Link>
          ))}

          {/* Center menu button */}
          <div className="relative flex flex-1 items-center justify-center">
            <button
              onClick={() => setMenuOpen(true)}
              className="flex h-14 w-14 -translate-y-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-background transition-transform active:scale-95"
            >
              <Building2 className="h-6 w-6" />
            </button>
          </div>

          {/* Right items */}
          {rightItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-1 py-2",
                isActive(item.to)
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-xs">{item.label}</span>
            </Link>
          ))}

          {/* Notifications */}
          <Link
            to="/notifications"
            className={cn(
              "relative flex flex-1 flex-col items-center justify-center gap-1 py-2",
              isActive("/notifications")
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute right-1/4 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
            <span className="text-xs">Bandeja</span>
          </Link>
        </div>
      </nav>

      <MobileMenuSheet open={menuOpen} onOpenChange={setMenuOpen} />
    </>
  );
}
